import { QueryClient } from '@tanstack/react-query'

/**
 * App-wide TanStack Query client.
 *
 * Defaults tuned for a mobile PWA:
 *   - `staleTime: 30s` — list views (dashboard, history) are
 *     reloaded on focus / network reconnect, but a back-and-forth
 *     navigation within 30 seconds reuses the cache. Cuts down
 *     API hits when a driver bounces between the dashboard and a
 *     mission detail.
 *   - `gcTime: 10 min` — cache lives long enough that a driver
 *     coming back from a phone-locked break sees their list
 *     immediately, then revalidates in the background.
 *   - `refetchOnWindowFocus: true` — picking the phone back up is
 *     the canonical "I want fresh data" signal on mobile.
 *   - `retry: 2` for queries — a flaky 4G dropout shouldn't break
 *     the UI; mutations stay at the default `0` because retrying
 *     POSTs/PATCHes risks duplicates (planning dismiss is
 *     idempotent but missions create is NOT).
 *
 * Pass 3 will swap this for an offline-aware client backed by
 * `@tanstack/query-persist-client-core` + IndexedDB. The shape of
 * the API here is the seam.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: true,
        retry: 2,
      },
      mutations: {
        retry: 0,
      },
    },
  })
}
